import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Settings, Users, BarChart3, Download, Plus, Trash2, Edit,
  Save, X, RefreshCw, Shield, FileSpreadsheet, Lightbulb,
  AlertTriangle, CheckCircle, TrendingUp, Calendar, ChevronDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import apiClient from '../api/client';
import { toast } from 'sonner';

// 3D effect gradient colors for charts
const CHART_GRADIENTS = [
  { id: 'gradient-blue', start: '#1e40af', end: '#3b82f6' },
  { id: 'gradient-amber', start: '#b45309', end: '#f59e0b' },
  { id: 'gradient-red', start: '#991b1b', end: '#ef4444' },
  { id: 'gradient-emerald', start: '#065f46', end: '#10b981' },
  { id: 'gradient-purple', start: '#5b21b6', end: '#8b5cf6' },
  { id: 'gradient-pink', start: '#9d174d', end: '#ec4899' },
];

const COLORS = ['#1e3a5f', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

// Custom 3D Bar Shape for modern look
const Custom3DBar = (props) => {
  const { x, y, width, height, fill, index } = props;
  const depth = 8;
  const gradients = ['url(#gradient-blue)', 'url(#gradient-amber)', 'url(#gradient-red)', 'url(#gradient-emerald)', 'url(#gradient-purple)', 'url(#gradient-pink)'];
  const gradientFill = gradients[index % gradients.length];
  
  return (
    <g>
      {/* Shadow */}
      <rect
        x={x + 3}
        y={y + 3}
        width={width}
        height={height}
        fill="rgba(0,0,0,0.15)"
        rx={4}
      />
      {/* Side face (3D effect) */}
      <polygon
        points={`${x + width},${y} ${x + width + depth},${y - depth} ${x + width + depth},${y + height - depth} ${x + width},${y + height}`}
        fill={fill}
        opacity={0.6}
      />
      {/* Top face (3D effect) */}
      <polygon
        points={`${x},${y} ${x + depth},${y - depth} ${x + width + depth},${y - depth} ${x + width},${y}`}
        fill={fill}
        opacity={0.8}
      />
      {/* Main bar */}
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={gradientFill}
        rx={4}
        style={{ filter: 'drop-shadow(2px 2px 3px rgba(0,0,0,0.2))' }}
      />
    </g>
  );
};

// Chart gradient definitions
const ChartGradients = () => (
  <defs>
    {CHART_GRADIENTS.map((g) => (
      <linearGradient key={g.id} id={g.id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={g.start} stopOpacity={1} />
        <stop offset="100%" stopColor={g.end} stopOpacity={0.8} />
      </linearGradient>
    ))}
  </defs>
);

const CommandCenter = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('reports');
  const [loading, setLoading] = useState(true);
  
  // Dropdown configs
  const [dropdownConfigs, setDropdownConfigs] = useState([]);
  const [editingConfig, setEditingConfig] = useState(null);
  const [newOption, setNewOption] = useState('');
  
  // Users
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  
  // Reports
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [reportData, setReportData] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [generatingSuggestions, setGeneratingSuggestions] = useState(false);
  const [trendAnalysis, setTrendAnalysis] = useState(null);
  const [generatingTrends, setGeneratingTrends] = useState(false);
  
  // New dropdown modal
  const [showNewDropdownModal, setShowNewDropdownModal] = useState(false);
  const [newDropdown, setNewDropdown] = useState({ name: '', display_name: '', module: 'General', options: [] });
  
  // New user modal
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [newUser, setNewUser] = useState({ 
    name: '', 
    email: '', 
    password: '', 
    role: 'user', 
    department: '', 
    job_title: '',
    is_active: true,
    is_department_manager: false,
    managed_departments: []
  });

  // BMS Departments
  const bmsDepartments = [
    { code: '0100', name: 'Sales and Estimating' },
    { code: '0200', name: 'Project Management' },
    { code: '0300', name: 'Supply Chain' },
    { code: '0400', name: 'Engineering' },
    { code: '0500', name: 'Manufacturing' },
    { code: '0600', name: 'Quality Control' },
    { code: '0700', name: 'Aftermarket' },
    { code: '0800', name: 'Accounts' },
    { code: '0900', name: 'BMS Administration' },
    { code: '1000', name: 'Human Resources' }
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [configsRes, usersRes] = await Promise.all([
        apiClient.get('/command-center/dropdown-configs'),
        apiClient.get('/command-center/users')
      ]);
      setDropdownConfigs(configsRes.data || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchReport = useCallback(async () => {
    try {
      const response = await apiClient.get(`/command-center/monthly-report/${selectedYear}/${selectedMonth}`);
      setReportData(response.data);
      setSuggestions(null);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to fetch report');
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'reports') {
      fetchReport();
    }
  }, [activeTab, fetchReport]);

  // Dropdown config handlers
  const handleSaveConfig = async (config) => {
    try {
      await apiClient.put(`/command-center/dropdown-configs/${config.id}`, {
        options: config.options
      });
      toast.success('Configuration saved');
      setEditingConfig(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save configuration');
    }
  };

  const handleAddOption = (config) => {
    if (!newOption.trim()) return;
    const updated = { ...config, options: [...config.options, newOption.trim()] };
    setEditingConfig(updated);
    setNewOption('');
  };

  const handleRemoveOption = (config, index) => {
    const updated = { ...config, options: config.options.filter((_, i) => i !== index) };
    setEditingConfig(updated);
  };

  const handleCreateDropdown = async () => {
    if (!newDropdown.name || !newDropdown.display_name) {
      toast.error('Name and display name are required');
      return;
    }
    try {
      await apiClient.post('/command-center/dropdown-configs', newDropdown);
      toast.success('Dropdown created');
      setShowNewDropdownModal(false);
      setNewDropdown({ name: '', display_name: '', module: 'General', options: [] });
      fetchData();
    } catch (error) {
      toast.error('Failed to create dropdown');
    }
  };

  const handleDeleteDropdown = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this dropdown configuration?')) return;
    try {
      await apiClient.delete(`/command-center/dropdown-configs/${configId}`);
      toast.success('Dropdown deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete dropdown');
    }
  };

  // User handlers
  const handleSaveUser = async (user) => {
    try {
      await apiClient.put(`/command-center/users/${user.id}`, {
        name: user.name,
        email: user.email,
        role: user.role,
        department: user.department,
        is_active: user.is_active
      });
      toast.success('User updated');
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      await apiClient.post(`/command-center/users/${userId}/reset-password`, { new_password: newPassword });
      toast.success('Password reset successfully');
      setShowResetPassword(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await apiClient.delete(`/command-center/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Name, email, and password are required');
      return;
    }
    if (newUser.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      // Include all fields including department manager settings
      const userData = {
        ...newUser,
        department_id: newUser.department, // Store department code as department_id
      };
      await apiClient.post('/command-center/users', userData);
      toast.success('User created successfully');
      setShowNewUserModal(false);
      setNewUser({ 
        name: '', 
        email: '', 
        password: '', 
        role: 'user', 
        department: '', 
        job_title: '',
        is_active: true,
        is_department_manager: false,
        managed_departments: []
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  // Report handlers
  const handleGenerateSuggestions = async () => {
    if (!reportData) return;
    setGeneratingSuggestions(true);
    try {
      const response = await apiClient.post('/command-center/generate-improvement-suggestions', reportData);
      setSuggestions(response.data);
    } catch (error) {
      toast.error('Failed to generate suggestions');
    } finally {
      setGeneratingSuggestions(false);
    }
  };

  const handleGenerateTrendAnalysis = async () => {
    setGeneratingTrends(true);
    try {
      // Fetch last 3 months of data for trend analysis
      const monthsData = [];
      for (let i = 0; i < 3; i++) {
        let m = selectedMonth - i;
        let y = selectedYear;
        if (m <= 0) {
          m += 12;
          y -= 1;
        }
        const response = await apiClient.get(`/command-center/monthly-report/${y}/${m}`);
        monthsData.push(response.data);
      }
      
      const response = await apiClient.post('/command-center/generate-trend-analysis', monthsData);
      setTrendAnalysis(response.data);
    } catch (error) {
      toast.error('Failed to generate trend analysis');
    } finally {
      setGeneratingTrends(false);
    }
  };

  const handleExportReport = async (module) => {
    try {
      const response = await apiClient.get(
        `/command-center/export-report/${selectedYear}/${selectedMonth}/${module}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${module}_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report exported');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  // Chart data preparation
  const prepareChartData = (dataObj) => {
    if (!dataObj) return [];
    return Object.entries(dataObj).map(([key, value]) => ({
      name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="space-y-6" data-testid="command-center-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white p-6 rounded-lg shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Shield className="w-10 h-10 text-blue-200" />
            <div>
              <h1 className="text-2xl font-bold">Command Center</h1>
              <p className="text-blue-200">Admin Dashboard - System Configuration & Reports</p>
            </div>
          </div>
          <Badge className="bg-red-500 text-white px-3 py-1">Admin Only</Badge>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-blue-50">
          <TabsTrigger value="reports" className="flex items-center gap-2 data-[state=active]:bg-blue-700 data-[state=active]:text-white">
            <BarChart3 className="w-4 h-4" />
            Monthly Reports
          </TabsTrigger>
          <TabsTrigger value="dropdowns" className="flex items-center gap-2 data-[state=active]:bg-blue-700 data-[state=active]:text-white">
            <Settings className="w-4 h-4" />
            Dropdown Menus
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2 data-[state=active]:bg-blue-700 data-[state=active]:text-white">
            <Users className="w-4 h-4" />
            User Management
          </TabsTrigger>
        </TabsList>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          {/* Date Selector */}
          <Card className="border-blue-100">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <Label>Select Period:</Label>
                </div>
                <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((month, idx) => (
                      <SelectItem key={idx} value={String(idx + 1)}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map((year) => (
                      <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={fetchReport} variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {reportData && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-slate-600">ONCR</p>
                        <p className="text-3xl font-bold text-blue-900">{reportData.oncr?.total || 0}</p>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => handleExportReport('oncr')}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-amber-500">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-slate-600">NCR</p>
                        <p className="text-3xl font-bold text-amber-700">{reportData.ncr?.total || 0}</p>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => handleExportReport('ncr')}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-red-500">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-slate-600">CAPA</p>
                        <p className="text-3xl font-bold text-red-700">{reportData.capa?.total || 0}</p>
                        {reportData.capa?.overdue > 0 && (
                          <p className="text-xs text-red-500">{reportData.capa.overdue} overdue</p>
                        )}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => handleExportReport('capa')}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-emerald-500">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-slate-600">Documents</p>
                        <p className="text-3xl font-bold text-emerald-700">{reportData.document_control?.total || 0}</p>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => handleExportReport('documents')}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Grid */}
              <div className="grid grid-cols-2 gap-6">
                {/* ONCR by Status - 3D Bar Chart */}
                <Card className="shadow-lg hover:shadow-xl transition-shadow">
                  <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-t-lg">
                    <CardTitle className="text-base text-blue-900">ONCR by Status</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={prepareChartData(reportData.oncr?.by_status)} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <ChartGradients />
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} />
                        <YAxis tick={{ fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'rgba(255,255,255,0.95)', 
                            borderRadius: '8px', 
                            border: 'none',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
                          }} 
                        />
                        <Bar dataKey="value" shape={<Custom3DBar />} fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* CAPA by Status - 3D Bar Chart */}
                <Card className="shadow-lg hover:shadow-xl transition-shadow">
                  <CardHeader className="bg-gradient-to-r from-red-50 to-red-100 rounded-t-lg">
                    <CardTitle className="text-base text-red-900">CAPA by Status</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={prepareChartData(reportData.capa?.by_status)} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <ChartGradients />
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} />
                        <YAxis tick={{ fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'rgba(255,255,255,0.95)', 
                            borderRadius: '8px', 
                            border: 'none',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
                          }} 
                        />
                        <Bar dataKey="value" shape={<Custom3DBar />} fill="#ef4444" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* CAPA by Department - 3D Bar Chart */}
                <Card className="shadow-lg hover:shadow-xl transition-shadow">
                  <CardHeader className="bg-gradient-to-r from-rose-50 to-rose-100 rounded-t-lg">
                    <CardTitle className="text-base text-rose-900">CAPA by Department</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={prepareChartData(reportData.capa?.by_department)} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <ChartGradients />
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} />
                        <YAxis tick={{ fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'rgba(255,255,255,0.95)', 
                            borderRadius: '8px', 
                            border: 'none',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
                          }} 
                        />
                        <Bar dataKey="value" shape={<Custom3DBar />} fill="#f43f5e" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* NCR by Severity - 3D Bar Chart */}
                <Card className="shadow-lg hover:shadow-xl transition-shadow">
                  <CardHeader className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-t-lg">
                    <CardTitle className="text-base text-amber-900">NCR by Severity</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={prepareChartData(reportData.ncr?.by_severity)} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <ChartGradients />
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} />
                        <YAxis tick={{ fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'rgba(255,255,255,0.95)', 
                            borderRadius: '8px', 
                            border: 'none',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
                          }} 
                        />
                        <Bar dataKey="value" shape={<Custom3DBar />} fill="#f59e0b" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Approval Metrics - NEW 3D Bar Chart */}
                <Card className="shadow-lg hover:shadow-xl transition-shadow">
                  <CardHeader className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-t-lg">
                    <CardTitle className="text-base text-purple-900">Approval Metrics</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart 
                        data={[
                          { name: 'ONCR Approvals', value: reportData.oncr?.closed || 0 },
                          { name: 'CAPA Approvals', value: reportData.capa?.closed || 0 },
                          { name: 'NCR Approvals', value: reportData.ncr?.closed || 0 },
                          { name: 'Document Approvals', value: reportData.document_control?.approved || 0 },
                        ]} 
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <ChartGradients />
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} />
                        <YAxis tick={{ fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'rgba(255,255,255,0.95)', 
                            borderRadius: '8px', 
                            border: 'none',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
                          }} 
                        />
                        <Bar dataKey="value" shape={<Custom3DBar />} fill="#8b5cf6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* AI Suggestions */}
              <Card className="border-blue-200 bg-blue-50/50">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-amber-500" />
                      <CardTitle>AI Improvement Suggestions</CardTitle>
                    </div>
                    <Button onClick={handleGenerateSuggestions} disabled={generatingSuggestions}>
                      {generatingSuggestions ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                      ) : (
                        <><Lightbulb className="w-4 h-4 mr-2" /> Generate Suggestions</>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                {suggestions && (
                  <CardContent className="space-y-4">
                    {/* Overall Assessment */}
                    <div className="p-4 bg-white rounded-lg border">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                        <h4 className="font-semibold">Overall Assessment</h4>
                        <Badge className={
                          suggestions.risk_level === 'high' ? 'bg-red-100 text-red-800' :
                          suggestions.risk_level === 'medium' ? 'bg-amber-100 text-amber-800' :
                          'bg-emerald-100 text-emerald-800'
                        }>
                          {suggestions.risk_level?.toUpperCase()} RISK
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-700">{suggestions.overall_assessment}</p>
                    </div>

                    {/* Suggestions List */}
                    <div className="space-y-3">
                      {suggestions.suggestions?.map((suggestion, idx) => (
                        <div key={idx} className={`p-4 rounded-lg border ${
                          suggestion.priority === 'high' ? 'border-red-200 bg-red-50' :
                          suggestion.priority === 'medium' ? 'border-amber-200 bg-amber-50' :
                          'border-blue-200 bg-blue-50'
                        }`}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{suggestion.module}</Badge>
                              <h5 className="font-medium">{suggestion.title}</h5>
                            </div>
                            <Badge className={
                              suggestion.priority === 'high' ? 'bg-red-600' :
                              suggestion.priority === 'medium' ? 'bg-amber-500' :
                              'bg-blue-500'
                            }>
                              {suggestion.priority}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-700 mb-2">{suggestion.description}</p>
                          <p className="text-xs text-slate-500">
                            <strong>Expected Impact:</strong> {suggestion.expected_impact}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Key Metrics */}
                    {suggestions.key_metrics_to_watch?.length > 0 && (
                      <div className="p-4 bg-white rounded-lg border">
                        <h4 className="font-semibold mb-2">Key Metrics to Watch</h4>
                        <div className="flex flex-wrap gap-2">
                          {suggestions.key_metrics_to_watch.map((metric, idx) => (
                            <Badge key={idx} variant="outline" className="bg-blue-50">
                              {metric}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>

              {/* Trend Analysis Section */}
              <Card className="border-emerald-200 bg-emerald-50/50">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                      <CardTitle>Multi-Month Trend Analysis</CardTitle>
                    </div>
                    <Button onClick={handleGenerateTrendAnalysis} disabled={generatingTrends} variant="outline" className="border-emerald-300">
                      {generatingTrends ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing Trends...</>
                      ) : (
                        <><TrendingUp className="w-4 h-4 mr-2" /> Analyze 3-Month Trends</>
                      )}
                    </Button>
                  </div>
                  <CardDescription>Compare data across the last 3 months to identify patterns and forecast risks</CardDescription>
                </CardHeader>
                {trendAnalysis && (
                  <CardContent className="space-y-4">
                    {/* Trend Summary */}
                    <div className="p-4 bg-white rounded-lg border">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-5 h-5 text-emerald-600" />
                        <h4 className="font-semibold">Trend Summary</h4>
                        <Badge className={
                          trendAnalysis.risk_trajectory === 'improving' ? 'bg-emerald-100 text-emerald-800' :
                          trendAnalysis.risk_trajectory === 'stable' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }>
                          {trendAnalysis.risk_trajectory?.toUpperCase()}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-700">{trendAnalysis.trend_summary}</p>
                    </div>

                    {/* Month-over-Month Changes */}
                    {trendAnalysis.month_over_month_changes?.length > 0 && (
                      <div className="grid grid-cols-2 gap-3">
                        {trendAnalysis.month_over_month_changes.map((change, idx) => (
                          <div key={idx} className={`p-3 rounded-lg border ${
                            change.trend === 'decreasing' ? 'border-emerald-200 bg-emerald-50' :
                            change.trend === 'increasing' ? 'border-red-200 bg-red-50' :
                            'border-blue-200 bg-blue-50'
                          }`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{change.metric}</span>
                              <Badge variant="outline" className="text-xs">
                                {change.percentage_change}
                              </Badge>
                            </div>
                            <p className="text-xs text-slate-600">{change.significance}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Emerging Concerns & Positive Trends */}
                    <div className="grid grid-cols-2 gap-4">
                      {trendAnalysis.emerging_concerns?.length > 0 && (
                        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                          <h5 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" /> Emerging Concerns
                          </h5>
                          <ul className="text-sm text-red-700 space-y-1">
                            {trendAnalysis.emerging_concerns.map((concern, idx) => (
                              <li key={idx}>• {concern}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {trendAnalysis.positive_trends?.length > 0 && (
                        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                          <h5 className="font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" /> Positive Trends
                          </h5>
                          <ul className="text-sm text-emerald-700 space-y-1">
                            {trendAnalysis.positive_trends.map((trend, idx) => (
                              <li key={idx}>• {trend}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Strategic Recommendations */}
                    {trendAnalysis.strategic_recommendations?.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="font-semibold">Strategic Recommendations</h4>
                        {trendAnalysis.strategic_recommendations.map((rec, idx) => (
                          <div key={idx} className={`p-4 rounded-lg border ${
                            rec.priority === 'immediate' ? 'border-red-200 bg-red-50' :
                            rec.priority === 'short_term' ? 'border-amber-200 bg-amber-50' :
                            'border-blue-200 bg-blue-50'
                          }`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">{rec.area}</Badge>
                                <span className="font-medium text-sm">{rec.recommendation}</span>
                              </div>
                              <Badge className={
                                rec.priority === 'immediate' ? 'bg-red-600' :
                                rec.priority === 'short_term' ? 'bg-amber-500' :
                                'bg-blue-500'
                              }>
                                {rec.priority?.replace('_', ' ')}
                              </Badge>
                            </div>
                            <p className="text-xs text-slate-600">
                              <strong>Expected Outcome:</strong> {rec.expected_outcome}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Forecast */}
                    {trendAnalysis.forecast && (
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <h5 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                          <Calendar className="w-4 h-4" /> Next Quarter Forecast
                        </h5>
                        <p className="text-sm text-blue-700">{trendAnalysis.forecast}</p>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            </>
          )}
        </TabsContent>

        {/* Dropdown Menus Tab */}
        <TabsContent value="dropdowns" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Dropdown Menu Configurations</h3>
            <Button onClick={() => setShowNewDropdownModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Dropdown
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {dropdownConfigs.map((config) => (
              <Card key={config.id} className="border-blue-100">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">{config.display_name}</CardTitle>
                      <CardDescription className="text-xs">
                        Module: {config.module} | Key: {config.name}
                      </CardDescription>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditingConfig({ ...config })}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteDropdown(config.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1">
                    {config.options?.map((option, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {option}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Edit Dropdown Modal */}
          <Dialog open={!!editingConfig} onOpenChange={() => setEditingConfig(null)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Edit: {editingConfig?.display_name}</DialogTitle>
              </DialogHeader>
              {editingConfig && (
                <div className="space-y-4">
                  <div>
                    <Label>Options</Label>
                    <div className="space-y-2 mt-2">
                      {editingConfig.options?.map((option, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Input value={option} readOnly className="flex-1" />
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleRemoveOption(editingConfig, idx)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      placeholder="Add new option..."
                      onKeyPress={(e) => e.key === 'Enter' && handleAddOption(editingConfig)}
                    />
                    <Button onClick={() => handleAddOption(editingConfig)}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingConfig(null)}>Cancel</Button>
                <Button onClick={() => handleSaveConfig(editingConfig)}>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* New Dropdown Modal */}
          <Dialog open={showNewDropdownModal} onOpenChange={setShowNewDropdownModal}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create New Dropdown</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Internal Name (key)</Label>
                  <Input
                    value={newDropdown.name}
                    onChange={(e) => setNewDropdown({ ...newDropdown, name: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                    placeholder="e.g., priority_levels"
                  />
                </div>
                <div>
                  <Label>Display Name</Label>
                  <Input
                    value={newDropdown.display_name}
                    onChange={(e) => setNewDropdown({ ...newDropdown, display_name: e.target.value })}
                    placeholder="e.g., Priority Levels"
                  />
                </div>
                <div>
                  <Label>Module</Label>
                  <Select value={newDropdown.module} onValueChange={(v) => setNewDropdown({ ...newDropdown, module: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="General">General</SelectItem>
                      <SelectItem value="IAMS">IAMS</SelectItem>
                      <SelectItem value="CAPA">CAPA</SelectItem>
                      <SelectItem value="NCR">NCR</SelectItem>
                      <SelectItem value="ONCR">ONCR</SelectItem>
                      <SelectItem value="Doc Control">Doc Control</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNewDropdownModal(false)}>Cancel</Button>
                <Button onClick={handleCreateDropdown}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Dropdown
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* User Management Tab */}
        <TabsContent value="users" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">User Management</h3>
            <div className="flex items-center gap-3">
              <Badge variant="outline">{users.length} Users</Badge>
              <Button onClick={() => setShowNewUserModal(true)} data-testid="create-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create User
              </Button>
            </div>
          </div>

          <Card>
            <CardContent className="pt-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-blue-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Name</th>
                      <th className="px-4 py-2 text-left">Email</th>
                      <th className="px-4 py-2 text-left">Role</th>
                      <th className="px-4 py-2 text-left">Department</th>
                      <th className="px-4 py-2 text-center">Status</th>
                      <th className="px-4 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium">{user.name}</td>
                        <td className="px-4 py-3 text-slate-600">{user.email}</td>
                        <td className="px-4 py-3">
                          <Badge className={
                            user.role === 'admin' ? 'bg-red-100 text-red-800' :
                            user.role === 'qa_manager' ? 'bg-purple-100 text-purple-800' :
                            user.role === 'manager' ? 'bg-blue-100 text-blue-800' :
                            'bg-slate-100 text-slate-800'
                          }>
                            {user.role?.replace('_', ' ').toUpperCase()}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{user.department || '-'}</td>
                        <td className="px-4 py-3 text-center">
                          {user.is_active !== false ? (
                            <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">Inactive</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => setEditingUser({ ...user })}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setShowResetPassword(user.id)}>
                              <Shield className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteUser(user.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Edit User Modal */}
          <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Edit User</DialogTitle>
              </DialogHeader>
              {editingUser && (
                <div className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={editingUser.name || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      value={editingUser.email || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Role</Label>
                    <Select value={editingUser.role} onValueChange={(v) => setEditingUser({ ...editingUser, role: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="qa_manager">QA Manager</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="user">User</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Department</Label>
                    <Input
                      value={editingUser.department || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, department: e.target.value })}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingUser.is_active !== false}
                      onChange={(e) => setEditingUser({ ...editingUser, is_active: e.target.checked })}
                      className="rounded"
                    />
                    <Label>Active</Label>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
                <Button onClick={() => handleSaveUser(editingUser)}>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Reset Password Modal */}
          <Dialog open={!!showResetPassword} onOpenChange={() => { setShowResetPassword(null); setNewPassword(''); }}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Reset Password</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>New Password</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 8 chars)"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setShowResetPassword(null); setNewPassword(''); }}>Cancel</Button>
                <Button onClick={() => handleResetPassword(showResetPassword)}>
                  <Shield className="w-4 h-4 mr-2" />
                  Reset Password
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Create User Modal */}
          <Dialog open={showNewUserModal} onOpenChange={setShowNewUserModal}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="user@company.com"
                  />
                </div>
                <div>
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="Min 8 characters"
                  />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="qa_manager">QA Manager</SelectItem>
                      <SelectItem value="manager">Department Manager</SelectItem>
                      <SelectItem value="engineer">Engineer</SelectItem>
                      <SelectItem value="technician">Technician</SelectItem>
                      <SelectItem value="quality_analyst">Quality Analyst</SelectItem>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                      <SelectItem value="requester">Requester</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Department (BMS Folder)</Label>
                  <Select value={newUser.department} onValueChange={(v) => setNewUser({ ...newUser, department: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {bmsDepartments.map(dept => (
                        <SelectItem key={dept.code} value={dept.code}>
                          {dept.code} - {dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Job Title</Label>
                  <Input
                    value={newUser.job_title || ''}
                    onChange={(e) => setNewUser({ ...newUser, job_title: e.target.value })}
                    placeholder="e.g. QC Manager, Project Lead"
                  />
                </div>
                {(newUser.role === 'manager' || newUser.role === 'admin' || newUser.role === 'qa_manager') && (
                  <div className="col-span-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-3">
                      <input
                        type="checkbox"
                        id="is_dept_manager"
                        checked={newUser.is_department_manager}
                        onChange={(e) => setNewUser({ ...newUser, is_department_manager: e.target.checked })}
                        className="rounded border-blue-300"
                      />
                      <Label htmlFor="is_dept_manager" className="text-blue-800 font-medium">
                        Department Manager (Receives approval requests)
                      </Label>
                    </div>
                    {newUser.is_department_manager && (
                      <div>
                        <Label className="text-blue-700 text-sm">Managed Departments</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {bmsDepartments.map(dept => (
                            <label key={dept.code} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={newUser.managed_departments?.includes(dept.code)}
                                onChange={(e) => {
                                  const current = newUser.managed_departments || [];
                                  if (e.target.checked) {
                                    setNewUser({ ...newUser, managed_departments: [...current, dept.code] });
                                  } else {
                                    setNewUser({ ...newUser, managed_departments: current.filter(d => d !== dept.code) });
                                  }
                                }}
                                className="rounded border-blue-300"
                              />
                              <span className="text-blue-800">{dept.code} - {dept.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newUser.is_active}
                    onChange={(e) => setNewUser({ ...newUser, is_active: e.target.checked })}
                    className="rounded"
                  />
                  <Label>Active</Label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNewUserModal(false)}>Cancel</Button>
                <Button onClick={handleCreateUser} data-testid="submit-create-user-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create User
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CommandCenter;
